<script setup lang="ts">
import HelpArticleView from './HelpArticleView.vue';
import HelpHeader from './HelpHeader.vue';
import HelpMenu from './HelpMenu.vue';
import HelpSupportForm from './HelpSupportForm.vue';
import { AppSidePanel } from '@app/components/parts/appSidePanel';
import PageGuide from '@app/components/pageGuide/pageGuide.vue';
import { usePageGuide } from '@app/composables/usePageGuide';
import Button from '@ui/components/forms/button.vue';
import Icon from '@ui/components/icon.vue';
import { computed, onUnmounted, watch } from 'vue';
import { useHelp } from './useHelp';
import {
    DEFAULT_PANEL_WIDTH,
    MAX_PANEL_WIDTH,
    MIN_PANEL_WIDTH,
    PANEL_WIDTH_STORAGE_KEY,
} from './useHelpConstants';
import { useBrowserInfo } from '@ui/composables/useBrowserInfo';

const helpNewsGuideName = 'dialog-help-mini-news';
const helpNewsGuideNamespace = 'helpMiniPageGuide';
const {
    shouldRender,
    namespace,
    isMenuSection,
    isArticleSection,
    isSupportSection,
    helpData,
    openSupportForm,
    apiError,
    submitSuccess,
    closeSubmitFeedback,
    isCapturingScreenshot,
    showSupportBlock,
    currentContentType,
    currentNewsWasUnread,
} = useHelp();
const { setPageGuide } = usePageGuide(helpNewsGuideNamespace);

const { isMacBased } = useBrowserInfo();

const texts = computed(() => helpData.value.texts);

const keyboardShortcut = computed(() => `${isMacBased.value ? 'Cmd' : 'Ctrl'} + Shift + ?`);
const shouldHighlightNews = computed(() =>
    shouldRender.value
    && currentContentType.value === 'news'
    && currentNewsWasUnread.value,
);
const newsGuide = computed(() => ({
    show: shouldHighlightNews.value,
    showTooltip: false,
    theme: 'yellow' as const,
    rounded: '2xl' as const,
    offset: 'lg' as const,
}));

watch(
    newsGuide,
    (value) => {
        setPageGuide(helpNewsGuideName, value);
    },
    { immediate: true, deep: true },
);

onUnmounted(() => {
    setPageGuide(helpNewsGuideName, { show: false });
});
</script>

<template>
    <AppSidePanel
        :namespace="namespace"
        :width-storage-key="PANEL_WIDTH_STORAGE_KEY"
        :default-width="DEFAULT_PANEL_WIDTH"
        :min-width="MIN_PANEL_WIDTH"
        :max-width="MAX_PANEL_WIDTH"
        :hidden="isCapturingScreenshot"
        :ui="{ root: 'help-panel', panel: '@container/help' }"
    >
        <PageGuide
            :name="helpNewsGuideName"
            :namespace="helpNewsGuideNamespace"
            class="flex min-h-0 flex-1 flex-col"
        >
            <div class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-black/5">
                <HelpHeader></HelpHeader>

                <HelpMenu v-if="isMenuSection"></HelpMenu>

                <HelpArticleView v-else-if="isArticleSection"></HelpArticleView>

                <HelpSupportForm v-else-if="isSupportSection"></HelpSupportForm>
                <div
                    v-if="!isSupportSection"
                    class="mt-auto border-t border-gray-200 bg-gray-50 px-2 pb-3 pt-2 text-center font-sans text-sm leading-6 text-gray-500"
                >
                    {{ texts.common.keyboardShortcutText }} <br>
                    <kbd class="inline-block rounded bg-white px-2 py-1 text-xs leading-3 text-gray-400 shadow-sm ring-1 ring-black/5">{{ keyboardShortcut }}</kbd>
                </div>
            </div>
        </PageGuide>
        <div
            v-if="isMenuSection && showSupportBlock"
            class="flex shrink-0 flex-col overflow-hidden rounded-lg bg-white p-4 shadow-md ring-1 ring-black/5"
        >
            <template v-if="apiError || submitSuccess">
                <div
                    v-if="apiError"
                    class="flex items-center gap-2 rounded bg-red-50 p-2 text-sm text-red-600 ring-1 ring-red-200"
                >
                    <Icon
                        icon="fas fa-times-circle"
                        size="small"
                        class="text-red-600"
                    ></Icon>
                    {{ apiError }}
                    <Icon
                        icon="fas fa-times"
                        size="small"
                        class="ml-auto cursor-pointer text-red-600"
                        @click="closeSubmitFeedback"
                    ></Icon>
                </div>
                <div
                    v-if="submitSuccess"
                    class="flex items-center gap-2 rounded bg-green-50 p-2 text-sm text-green-600 ring-1 ring-green-200"
                >
                    <Icon
                        icon="fas fa-check-circle"
                        size="small"
                        class="text-green-600"
                    ></Icon>
                    {{ texts.menu.successMessage }}
                    <Icon
                        icon="fas fa-times"
                        size="small"
                        class="ml-auto cursor-pointer text-green-600"
                        @click="closeSubmitFeedback"
                    ></Icon>
                </div>
            </template>
            <div
                v-else
                class="@[448px]/help:flex-row @[448px]/help:items-center flex flex-col gap-x-4 gap-y-3"
            >
                <div class="grow">
                    <h2 class="font-brand m-0 text-base font-semibold text-gray-900">
                        {{ texts.menu.supportBlockTitle }}
                    </h2>
                    <div class="font-sans text-sm text-gray-600">
                        {{ texts.menu.supportBlockDescription }}
                    </div>
                </div>
                <Button
                    theme="primary"
                    size="medium"
                    class="shrink-0"
                    icon="far fa-headset"
                    @click="openSupportForm"
                >
                    {{ texts.menu.supportBlockButton }}
                </Button>
            </div>
        </div>
    </AppSidePanel>
</template>
